import React, { useEffect, useRef, useState } from 'react';

// This surface only consumes WHEP tracks. Capture preview is never an input.
export default function OwnerRoundTripViewer({ viewer, sessionId }) {
  const video = useRef(null);
  const [stream, setStream] = useState(null);
  const [received, setReceived] = useState(false);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let active = true, transport, unsubscribe, timer;
    setStream(null); setReceived(false); setFailed(false);
    const fail = () => {
      if (!active) return;
      setStream(null); setReceived(false); setFailed(true);
      void transport?.dispose();
    };
    (async () => {
      try {
        const grant = await viewer.getGrant(sessionId);
        if (!active) return;
        viewer.assertCurrent();
        const remaining = Date.parse(grant.expiresAt) - Date.now();
        if (grant.role !== 'read' || !(remaining > 0)) throw new Error('No current server view');
        timer = setTimeout(fail, Math.min(remaining, 2147483647));
        transport = viewer.transportFactory({ role: 'view', authorizedMediaOrigin: viewer.mediaOrigin, videoOnly: true });
        unsubscribe = transport.subscribe(value => {
          if (!active) return;
          try {
            viewer.assertCurrent();
            if (['error', 'stopped'].includes(value.status)) { fail(); return; }
            if (value.stream?.getVideoTracks().length) setStream(value.stream);
          } catch { fail(); }
        });
        await transport.start({ endpoint: grant.whepUrl, token: grant.token });
      } catch { fail(); }
    })();
    return () => { active = false; clearTimeout(timer); unsubscribe?.(); void transport?.dispose(); };
  }, [viewer, sessionId]);
  useEffect(() => {
    const element = video.current;
    let active = true, callback;
    setReceived(false); element.srcObject = stream;
    if (stream && element.requestVideoFrameCallback) callback = element.requestVideoFrameCallback((_now, metadata) => {
      if (active && metadata.presentedFrames > 0) setReceived(true);
    });
    return () => { active = false; if (callback !== undefined) element.cancelVideoFrameCallback?.(callback); element.srcObject = null; };
  }, [stream]);
  return <section aria-label="Server return video">
    <h3>Server return</h3>
    <video ref={video} aria-label="Server return" className="camera-preview-video" autoPlay muted playsInline
      onLoadedData={() => { const v = video.current; if (stream && v.readyState >= 2 && v.videoWidth > 0) setReceived(true); }} />
    <p role="status">{failed ? 'Server video unavailable' : received ? 'Server video received' : 'Waiting for server video…'}</p>
  </section>;
}
